import { Box, Group, Paper, Progress, Text } from '@mantine/core';
import type { FC, PropsWithChildren } from 'react';
import { useHostContext } from '@/context/HostContext.tsx';
import { cn } from '@/utils';

enum CreatorFlowStepStatus {
	Active = 'active',
	Complete = 'complete',
	Disabled = 'disabled'
}

enum CreatorFlowStepKey {
	GettingToKnowYourBusiness = 'getting_to_know_your_business',
	InitialAgentCustomization = 'initial_agent_customization',
	AccountSetup = 'account_setup',
	ExploringAgentCapabilities = 'exploring_agent_capabilities',
	QuickAgentTest = 'quick_agent_test',
	ScheduleMeetingWalkthrough = 'schedule_a_meeting_for_a_full_walkthrough',
	Review = 'review'
}

interface CreatorFlowStep {
	key: CreatorFlowStepKey;
	progressValue: number;
	status: CreatorFlowStepStatus;
}

type CreatorFlowLayoutProps = PropsWithChildren<{
	steps: CreatorFlowStep[];
}>;

const progressTrackColorByStatus: Record<CreatorFlowStepStatus, string> = {
	[CreatorFlowStepStatus.Active]: 'rgba(255, 255, 255, 0.15)',
	[CreatorFlowStepStatus.Complete]: 'emerald.1',
	[CreatorFlowStepStatus.Disabled]: 'gray.2'
};

const progressColorByStatus: Record<CreatorFlowStepStatus, string> = {
	[CreatorFlowStepStatus.Active]: 'white',
	[CreatorFlowStepStatus.Complete]: 'emerald.5',
	[CreatorFlowStepStatus.Disabled]: 'gray.4'
};

const CreatorFlowLayout: FC<CreatorFlowLayoutProps> = ({ children, steps }) => {
	const { localization } = useHostContext();

	return (
		<Box
			className="mx-auto flex h-full min-h-0 w-full max-w-[1440px] flex-col gap-6 lg:grid lg:grid-cols-[320px_minmax(0,1fr)] lg:gap-8 xl:grid-cols-[360px_minmax(0,1fr)]"
			data-testid="creator-flow-layout"
		>
			<Box
				className="rounded-[28px] border border-slate-200 bg-gradient-to-b from-white to-slate-50 p-4 shadow-[0_20px_60px_rgba(15,23,42,0.06)] sm:p-5 lg:sticky lg:top-8"
				component="aside"
				data-testid="creator-flow-sidebar"
			>
				<Box
					className="m-0 flex list-none flex-col gap-3 p-0"
					component="ol"
				>
					{steps.map((step, index) => {
						const isActive = step.status === CreatorFlowStepStatus.Active;
						const isComplete = step.status === CreatorFlowStepStatus.Complete;
						const isDisabled = step.status === CreatorFlowStepStatus.Disabled;

						return (
							<Paper
								aria-current={isActive ? 'step' : undefined}
								aria-disabled={isDisabled || undefined}
								className={cn(
									'rounded-[24px] border px-4 py-4 transition-colors duration-200',
									{
										'border-slate-900 bg-slate-950 text-white shadow-[0_16px_40px_rgba(15,23,42,0.18)]':
											isActive,
										'border-emerald-200 bg-emerald-50 text-slate-900':
											isComplete,
										'border-slate-200 bg-white text-slate-500': isDisabled
									}
								)}
								component="li"
								data-status={step.status}
								data-testid={`creator-flow-step-${step.key}`}
								key={step.key}
								shadow="none"
							>
								<Group
									align="flex-start"
									gap={12}
									wrap="nowrap"
								>
									<Box
										className={cn(
											'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-sm font-semibold',
											{
												'bg-white/15 text-white ring-1 ring-white/20': isActive,
												'bg-emerald-100 text-emerald-700': isComplete,
												'bg-slate-100 text-slate-500': isDisabled
											}
										)}
									>
										{index + 1}
									</Box>

									<Box className="min-w-0 flex-1">
										<Text
											className={cn(
												'text-sm font-semibold leading-5 [font-family:var(--font-headlines)]',
												{
													'text-white': isActive,
													'text-slate-900': isComplete,
													'text-slate-500': isDisabled
												}
											)}
											data-testid="creator-flow-step-label"
										>
											{localization.translate(
												'creator',
												`flowSidebar.steps.${step.key}`
											)}
										</Text>

										<Progress
											bg={progressTrackColorByStatus[step.status]}
											className="mt-3"
											color={progressColorByStatus[step.status]}
											data-testid="creator-flow-step-progress"
											radius="xl"
											size={6}
											value={Math.max(0, Math.min(step.progressValue, 100))}
										/>
									</Box>
								</Group>
							</Paper>
						);
					})}
				</Box>
			</Box>

			<Paper
				className="min-h-[420px] overflow-hidden rounded-[32px] border border-slate-200 bg-white shadow-[0_24px_80px_rgba(15,23,42,0.08)] lg:min-h-[560px]"
				component="section"
				data-testid="creator-flow-content"
				shadow="none"
			>
				<Box className="flex h-full min-h-[420px] flex-col p-4 sm:p-6 lg:min-h-[560px] lg:p-8">
					{children}
				</Box>
			</Paper>
		</Box>
	);
};

export default CreatorFlowLayout;
export { CreatorFlowStepKey, CreatorFlowStepStatus };
export type { CreatorFlowStep };
